import { ADMIN_API, fetchWithAccount } from '@/api/client'
import { listDemoAdjustments, createDemoAdjustment, decideDemoAdjustment } from '../data/dummyData'
import type { UsageAdjustmentRequest, CreateUsageAdjustmentInput, DecideUsageAdjustmentInput } from '../types/usageAdjustment.types'

/**
 * Real backend is live (fh-admin-portal-api: UsageAdjustmentController, MongoDB-backed
 * usage_adjustment_requests collection). DEMO_MODE stays here only as a local-dev escape
 * hatch (falls back to data/dummyData.ts's localStorage store) — it must be false in any
 * committed/deployed build.
 *
 * Approving a request (decideUsageAdjustment) publishes a real ingress_adjustment/
 * outgress_adjustment event to the shared system-raw Kafka topic — see
 * UsageAdjustmentService.emitAdjustmentEvent() on the backend. Not yet reportable downstream
 * though: cdp_config.event_mapping has no registered mapping for these event names yet (unlike
 * data_export's), so daily_entity_event_metrics won't decode the event's fields until one exists.
 */
const DEMO_MODE = false

const USAGE_ADJUSTMENTS_API = `${ADMIN_API}/admin/usage-adjustments`

export async function listUsageAdjustments(accountId: string): Promise<UsageAdjustmentRequest[]> {
  if (DEMO_MODE) {
    return listDemoAdjustments(Number(accountId))
  }
  return fetchWithAccount<UsageAdjustmentRequest[]>(USAGE_ADJUSTMENTS_API, accountId)
}

export async function createUsageAdjustment(
  accountId: string,
  input: CreateUsageAdjustmentInput,
  requestedBy: string,
): Promise<UsageAdjustmentRequest> {
  if (DEMO_MODE) {
    return createDemoAdjustment(Number(accountId), input, requestedBy)
  }
  return fetchWithAccount<UsageAdjustmentRequest>(USAGE_ADJUSTMENTS_API, accountId, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

/** Admin-only. Approving fires the Kafka event emission; rejecting does not. */
export async function decideUsageAdjustment(
  accountId: string,
  id: string,
  input: DecideUsageAdjustmentInput,
): Promise<UsageAdjustmentRequest> {
  if (DEMO_MODE) {
    return decideDemoAdjustment(Number(accountId), id, input)
  }
  return fetchWithAccount<UsageAdjustmentRequest>(`${USAGE_ADJUSTMENTS_API}/${id}/decision`, accountId, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}
